import { useEffect, useReducer, useState } from "react";
import { collection, addDoc, deleteDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { projectFirestore } from "../firebase/config";

// Initial state
const initialState = {
  document: null,
  isPending: false,
  error: null,
  success: false,
};

// Reducer
const firestoreReducer = (state, action) => {
  // Handle different actions
  switch (action.type) {
    case "IS_PENDING":
      return { isPending: true, document: null, success: false, error: null }; // Set pending state
    case "ADDED_DOCUMENT":
      return { isPending: false, document: action.payload, success: true, error: null }; // Document added successfully
    case "DELETED_DOCUMENT":
      return { isPending: false, document: null, success: true, error: null }; // Document deleted successfully
    case "UPDATED_DOCUMENT":
      return { isPending: false, document: action.payload, success: true, error: null }; // Document updated successfully
    case "ERROR":
      return { isPending: false, document: null, success: false, error: action.payload }; // Error occurred
    default:
      return state;
  }
};

export const useFirestore = (collectionName) => {
  // Use reducer to manage state
  const [response, dispatch] = useReducer(firestoreReducer, initialState);
  const [isCancelled, setIsCancelled] = useState(false);

  const dispatchIfNotCancelled = (action) => {
    if (!isCancelled) dispatch(action);
  };

  // Add document
  const addDocument = async (docData) => {
    dispatch({ type: "IS_PENDING" });
    try {
      const createdAt = serverTimestamp();
      const docRef = await addDoc(collection(projectFirestore, collectionName), {
        ...docData,
        createdAt,
        lastModified: createdAt,
      });
      dispatchIfNotCancelled({ type: "ADDED_DOCUMENT", payload: docRef });
      return docRef;
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
    }
  };

  // Delete document
  const deleteDocument = async (id) => {
    dispatch({ type: "IS_PENDING" });
    try {
      await deleteDoc(doc(projectFirestore, collectionName, id));
      dispatchIfNotCancelled({ type: "DELETED_DOCUMENT" });
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
    }
  };

  // Update document
  const updateDocument = async (id, updates) => {
    dispatch({ type: "IS_PENDING" });
    try {
      const docRef = doc(projectFirestore, collectionName, id);
      await updateDoc(docRef, updates);
      dispatchIfNotCancelled({ type: "UPDATED_DOCUMENT", payload: docRef });
    } catch (err) {
      dispatchIfNotCancelled({ type: "ERROR", payload: err.message });
    }
  };

  useEffect(() => {
    setIsCancelled(false);
    // Cleanup function
    return () => setIsCancelled(true);
  }, []);

  return { addDocument, deleteDocument, updateDocument, response };
};
